import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../lib/motion";
import { COMPANY, BRANDS, PACKAGING_OPTIONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "company", label: "Company", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone / WhatsApp", type: "tel" },
  { name: "destination", label: "Destination Country", type: "text" },
  { name: "product_interest", label: "Product of Interest", type: "text" },
  { name: "quantity", label: "Quantity", type: "text" },
  { name: "grade", label: "Grade Preference", type: "text" },
];

export default function Contact({ defaultBrand = "" }) {
  const empty = {
    name: "", company: "", country: "", email: "", phone: "",
    brand: defaultBrand, product_interest: "", quantity: "", grade: "",
    packaging: "", destination: "", message: "", hp: "",
  };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Thank you. Our team will respond within 24 business hours.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#111] py-24 md:py-40 grain relative overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal><p className="overline text-[#B08D57] mb-6">07 — The Invitation</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="section-title font-display text-[#F8F7F3] mb-8">
              Let&apos;s Build a Partnership That Lasts
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-lg text-white/70 leading-relaxed max-w-[460px]">
              Whether you are sourcing spices, coffee, coconut products, or customized food
              ingredients, our team is ready to support your international procurement requirements.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="overline text-white/40 mb-2">Direct Enquiries</p>
              <a href="mailto:hello@agragavelora.com" className="font-display text-2xl text-[#F8F7F3] link-underline">
                hello@agragavelora.com
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-8" data-testid="contact-address">
              <p className="overline text-white/40 mb-3">Registered Office</p>
              <address className="not-italic font-body text-[15px] text-white/70 leading-relaxed">
                <span className="block text-white/90">{COMPANY.legalName}</span>
                {COMPANY.addressLines.map((line) => (
                  <span key={line} className="block">{line}</span>
                ))}
              </address>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
            {/* honeypot — hidden from users, traps bots */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp}
              onChange={set("hp")}
              style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
            />
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label className="overline text-white/50 text-[11px] block mb-3">Brand</label>
                <select
                  data-testid="field-brand"
                  value={form.brand}
                  onChange={set("brand")}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] font-body text-base outline-none transition-colors duration-300 focus:border-[#B08D57]"
                >
                  <option value="" className="bg-[#111]">Any / Not sure</option>
                  {BRANDS.map((b) => (
                    <option key={b.key} value={b.name} className="bg-[#111]">{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline text-white/50 text-[11px] block mb-3">Packaging</label>
                <select
                  data-testid="field-packaging"
                  value={form.packaging}
                  onChange={set("packaging")}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] font-body text-base outline-none transition-colors duration-300 focus:border-[#B08D57]"
                >
                  <option value="" className="bg-[#111]">Select packaging</option>
                  {PACKAGING_OPTIONS.map((p) => (
                    <option key={p} value={p} className="bg-[#111]">{p}</option>
                  ))}
                </select>
              </div>
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "product_interest" ? "sm:col-span-2" : ""}>
                  <label className="overline text-white/50 text-[11px] block mb-3">
                    {f.label}{f.required ? " *" : ""}
                  </label>
                  <input
                    data-testid={`field-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={set(f.name)}
                    className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] font-body text-base outline-none transition-colors duration-300 focus:border-[#B08D57] placeholder:text-white/30"
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="overline text-white/50 text-[11px] block mb-3">Message</label>
              <textarea
                data-testid="field-message"
                rows={3}
                value={form.message}
                onChange={set("message")}
                className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] font-body text-base outline-none resize-none transition-colors duration-300 focus:border-[#B08D57]"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full sm:w-auto rounded-full bg-[#F8F7F3] text-[#111] font-head font-semibold px-10 py-4 transition-colors duration-300 hover:bg-[#B08D57] hover:text-white disabled:opacity-60"
            >
              {loading ? "Sending…" : "Request a Quote"}
            </button>
            <p className="font-body text-sm text-white/40">Response within 24 business hours.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
